import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { Effective } from '../../types/shared';
import { Button } from '../ui/Button';
import { EffectiveChoice, Field, Toggle } from '../ui/Form';
export function GoalsSettings() {
  const {
    family,
    activeMembers,
    updateRules,
    togglePersonalGoal,
    addInstrument
  } = useAppState();
  const r = family.rules;
  const [vals, setVals] = useState({
    readingMinutes: r.readingMinutes,
    sportMinutes: r.sportMinutes,
    sportPerWeekChild: r.sportPerWeekChild,
    sportPerWeekChildWithSchoolPE: r.sportPerWeekChildWithSchoolPE,
    sportPerWeekAdult: r.sportPerWeekAdult
  });
  const [effective, setEffective] = useState<Effective>('naslednji-teden');
  const [instMember, setInstMember] = useState(activeMembers.find(m => m.role === 'otrok')?.id ?? '');
  const [instName, setInstName] = useState('');
  useEffect(() => setVals({
    readingMinutes: r.readingMinutes,
    sportMinutes: r.sportMinutes,
    sportPerWeekChild: r.sportPerWeekChild,
    sportPerWeekChildWithSchoolPE: r.sportPerWeekChildWithSchoolPE,
    sportPerWeekAdult: r.sportPerWeekAdult
  }), [r]);
  const dirty = (Object.keys(vals) as (keyof typeof vals)[]).some(k => vals[k] !== r[k]);
  const num = (key: keyof typeof vals, label: string, suffix: string) => <Field label={label} data-id="element-562" data-mp="2x8pb2efvweaj">
      {id => <div className="flex items-center gap-2" data-id="element-563" data-mp="3c79j28jqekas">
          <input id={id} type="number" min={0} className="field" value={vals[key]} onChange={e => setVals(v => ({
        ...v,
        [key]: Math.max(0, Number(e.target.value))
      }))} data-id="element-564" data-mp="3c5ybryr2uuex" />
          <span className="text-sm text-muted" data-id="element-565" data-mp="3kyrvq9hgyhz4">{suffix}</span>
        </div>}
    </Field>;
  const has = (memberId: string, key: 'branje' | 'sport') => family.templates.some(t => t.id === `${key}-${memberId}`);
  return <div className="space-y-6" data-id="element-566" data-mp="3uyaeimln7xpl">
      <section data-id="element-567" data-mp="3pz66hzagg60u">
        <h3 className="font-display text-lg font-semibold" data-id="element-568" data-mp="3pzfvpknoe95h">Pravila ciljev</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-569" data-mp="2nlhzlcaff6cw">
          {num('readingMinutes', 'Branje na dan', 'min')}
          {num('sportMinutes', 'Intenzivna vadba – najmanj', 'min')}
          {num('sportPerWeekChild', 'Otroci: vadb na teden', '×')}
          {num('sportPerWeekChildWithSchoolPE', 'Otroci s športno v šoli', '× zunaj šole')}
          {num('sportPerWeekAdult', 'Skrbniki: vadb na teden', '×')}
        </div>
        <p className="mt-2 text-xs text-muted" data-id="element-570" data-mp="36csoxp0i7b2o">
          Instrument: dnevna domača vadba odpade samo na dan dogodka vrste »Pouk instrumenta« za tega člana (ne solfeggio ali teorija). Branje, vadba in šport so na zaupanje – brez dokazil.
        </p>
        {dirty && <div className="mt-3 space-y-3" data-id="element-571" data-mp="3kbsinb2vig5p">
            <EffectiveChoice value={effective} onChange={setEffective} data-id="element-572" data-mp="3kahdb6ts50ch" />
            <Button onClick={() => updateRules(vals, 'Osebni cilji', `Branje ${r.readingMinutes}→${vals.readingMinutes} min, šport ${r.sportPerWeekChild}/${r.sportPerWeekChildWithSchoolPE}/${r.sportPerWeekAdult} → ${vals.sportPerWeekChild}/${vals.sportPerWeekChildWithSchoolPE}/${vals.sportPerWeekAdult}`, effective)} data-id="element-573" data-mp="1kz6eicd7sjab">
              Shrani pravila
            </Button>
          </div>}
      </section>

      <section data-id="element-574" data-mp="13n9d01oob4sv">
        <h3 className="font-display text-lg font-semibold" data-id="element-575" data-mp="21d2uw667e1l8">Komu velja</h3>
        <ul className="mt-2 divide-y divide-line rounded-3xl border-[1.5px] border-line bg-surface px-4" data-id="element-576" data-mp="21cb8nlyhxg7m">
          {activeMembers.map(m => <li key={m.id} className="grid gap-x-6 py-2 sm:grid-cols-[120px_1fr_1fr] sm:items-center" data-id="element-577" data-mp="1redmd4075thn">
              <span className="font-bold" data-id="element-578" data-mp="1ne3710qilqq4">{m.name}</span>
              <Toggle checked={has(m.id, 'branje')} onChange={v => togglePersonalGoal(m.id, 'branje', v)} label="Branje" data-id="element-579" data-mp="1nfeebaj65glz" />
              <Toggle checked={has(m.id, 'sport')} onChange={v => togglePersonalGoal(m.id, 'sport', v)} label="Šport" data-id="element-580" data-mp="byei43tpahmo" />
            </li>)}
        </ul>
      </section>

      <section data-id="element-581" data-mp="1zt1915fwspvn">
        <h3 className="font-display text-lg font-semibold" data-id="element-582" data-mp="24lax0fxgrfva">Dodaj instrument</h3>
        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-id="element-583" data-mp="24ndxjtw8sre9">
          <Field label="Član" data-id="element-584" data-mp="1pyhnxsejf6bj">
            {id => <select id={id} className="field" value={instMember} onChange={e => setInstMember(e.target.value)} data-id="element-585" data-mp="1uuzumxtfheqi">
                {activeMembers.map(m => <option key={m.id} value={m.id} data-id="element-586" data-mp="2nkf3bgxmevj9">
                    {m.name}
                  </option>)}
              </select>}
          </Field>
          <Field label="Instrument" data-id="element-587" data-mp="29kdzptpz1a03">{id => <input id={id} className="field" value={instName} onChange={e => setInstName(e.target.value)} placeholder="npr. Klavir" data-id="element-588" data-mp="2dlzan9iy3e7n" />}</Field>
          <Button variant="secondary" disabled={!instName.trim() || !instMember} onClick={() => {
          addInstrument(instMember, instName.trim());
          setInstName('');
        }} data-id="element-589" data-mp="y4phffnnwz5m">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-590" data-mp="poi05aou28zz" /> Dodaj
          </Button>
        </div>
      </section>
    </div>;
}